import {
  formatDateOrdinal,
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import { type JobActivity } from '@airship-platform/jobs-activities-feature/schema';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { MergedJobActivity } from './jobsActivities.models';
import {
  initState,
  jobsActivitiesReducer,
  JobsActivitiesState,
} from './jobsActivities.reducer';

interface JobsActivitiesTrackingContextProps {
  jobsActivities: JobActivity[];
  jobsActivitiesGroupedByDate: Record<string, MergedJobActivity[]>;
  isLoading: boolean;
  isLoaded: boolean;
  fetchJobsActivities: (
    appointmentId: string,
    appointmentProposalId: string
  ) => void;
}

const JobsActivitiesContext = createContext<
  JobsActivitiesTrackingContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<JobsActivitiesState>;
}>;

export const JobsActivitiesProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(jobsActivitiesReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const jobsActivities = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentProposalId === state.selectedProposalId &&
            a.appointmentId === state.selectedAppointmentId
        ),
    [
      state.entities,
      state.ids,
      state.selectedProposalId,
      state.selectedAppointmentId,
    ]
  );

  const jobsActivitiesGroupedByDate = useMemo(() => {
    const compressJobsActivities = (activities: JobActivity[]) => {
      const FIVE_MINUTES = 5 * 60 * 1000;
      const sorted = activities.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const result: MergedJobActivity[] = [];

      for (const activity of sorted) {
        const last = result[result.length - 1];

        if (
          last &&
          last.activityType === activity.activityType &&
          last.userId === activity.userId
        ) {
          const lastTime = new Date(last.createdAt).getTime();
          const currentTime = new Date(activity.createdAt).getTime();

          if (currentTime - lastTime <= FIVE_MINUTES) {
            last.count += 1;
            continue;
          }
        }

        result.push({
          ...activity,
          count: 1,
        });
      }

      return result;
    };

    const compressedJobsActivities = compressJobsActivities(jobsActivities);

    return compressedJobsActivities.reduce<Record<string, MergedJobActivity[]>>(
      (acc, activity) => {
        const date = formatDateOrdinal(activity.createdAt);
        acc[date] = [...(acc[date] || []), activity];
        return acc;
      },
      {}
    );
  }, [jobsActivities]);

  const fetchJobsActivities = useCallback(
    async (appointmentId: string, appointmentProposalId: string) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      try {
        dispatch({
          type: 'FETCH_JOBS_ACTIVITIES',
          payload: { appointmentId, appointmentProposalId },
        });

        const activities = await httpClient.get<JobActivity[]>(
          `/jobs-activities/appointments/${appointmentId}/proposals/${appointmentProposalId}/activities`
        );

        dispatch({
          type: 'FETCH_JOBS_ACTIVITIES_SUCCESS',
          payload: {
            activities: activities,
          },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_JOBS_ACTIVITIES_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient, isOnline]
  );

  return (
    <JobsActivitiesContext.Provider
      value={{
        jobsActivities,
        jobsActivitiesGroupedByDate,
        fetchJobsActivities,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
      }}
    >
      {children}
    </JobsActivitiesContext.Provider>
  );
};

export const useJobsActivities = () => {
  const context = useContext(JobsActivitiesContext);
  if (!context) {
    throw new Error(
      'useJobsActivities must be used within a JobsActivitiesProvider'
    );
  }
  return context;
};
